import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  backupSchema, createBackup, earnedByCurrency, formatMoney, isLive, projectCurrency, restoreOperations, trashItems,
  type Backup, type BusinessProfile, type Settings, type Theme
} from "@otrack/domain";
import { Button, EditableText, Field, FieldError, FileDrop, Icon, IconButton, Modal, Select, Switch, type IconName } from "@otrack/ui";
import { enabledCalendars, useGoogleState } from "../calendar/google";
import { defaultDestination, destinationOf, destinationOptions } from "../calendar/popover";
import { convertTyped, currencyOptions, MoneyField, PageHeader, Panel, rise, SettingRow } from "../components";
import { useApp, useDataset, useFactory } from "../context";
import { prepareLogo } from "../logo";
import { LogoPicker } from "../logo-picker";
import type { DesktopServices } from "../context";
import { href } from "../router";
import { updateAction, updateStatus } from "../update-pill";
import { formatClock, formatMonth, monthRange } from "../time";

const SECTIONS: Array<{ id: string; label: string; icon: IconName; sync?: boolean; desktop?: boolean }> = [
  { id: "sync", label: "Sync", icon: "cloud", sync: true },
  { id: "desktop", label: "Desktop", icon: "command", desktop: true },
  { id: "general", label: "General", icon: "monitor" },
  { id: "goals", label: "Goals", icon: "target" },
  { id: "calendars", label: "Calendar", icon: "calendar" },
  { id: "invoicing", label: "Invoicing", icon: "receipt" },
  { id: "data", label: "Your data", icon: "database" }
];

/** Distance from the viewport top at which a section counts as reached; matches the panels' scroll margin. */
const sectionLine = (element: HTMLElement) => (Number.parseFloat(getComputedStyle(element).scrollMarginTop) || 0) + 12;

/**
 * Which section is in view, for the side navigation. The last section whose top has passed the line wins, and the
 * bottom of the page always selects the last one. While a jump scrolls the page, the clicked section stays selected
 * so the indicator doesn't wander through the sections in between.
 */
function useActiveSection(ids: string[]): [string, (id: string) => void] {
  const [active, setActive] = useState(ids[0] ?? "");
  const jumping = useRef<number | undefined>(undefined);
  const key = ids.join(",");

  useEffect(() => {
    const keys = key.split(",");
    const update = () => {
      if (jumping.current !== undefined) {
        window.clearTimeout(jumping.current);
        jumping.current = window.setTimeout(() => { jumping.current = undefined; }, 140);
        return;
      }
      const elements = keys.map((id) => document.getElementById(`settings-${id}`)).filter((element) => element !== null);
      if (!elements.length) return;
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      const reached = atBottom ? elements.at(-1)! : elements.findLast((element) => element.getBoundingClientRect().top <= sectionLine(element)) ?? elements[0]!;
      setActive(reached.id.replace(/^settings-/, ""));
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      window.clearTimeout(jumping.current);
      jumping.current = undefined;
    };
  }, [key]);

  const jump = useCallback((id: string) => {
    const element = document.getElementById(`settings-${id}`);
    if (!element) return;
    setActive(id);
    window.clearTimeout(jumping.current);
    // Released once scrolling has been quiet for a moment; the timer restarts on every scroll event.
    jumping.current = window.setTimeout(() => { jumping.current = undefined; }, 140);
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    element.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }, []);

  return [active, jump];
}

type PillBox = { x: number; y: number; width: number; height: number };

/** Places the highlight under the active link, measured inside the nav so page scrolling never moves it. */
function useNavPill(nav: RefObject<HTMLElement | null>, active: string): PillBox | null {
  const [box, setBox] = useState<PillBox | null>(null);

  useLayoutEffect(() => {
    const element = nav.current;
    if (!element) return;
    const measure = () => {
      const link = element.querySelector<HTMLElement>(`[data-section="${active}"]`);
      if (!link) return setBox(null);
      const next = { x: link.offsetLeft, y: link.offsetTop, width: link.offsetWidth, height: link.offsetHeight };
      setBox((current) => current && current.x === next.x && current.y === next.y && current.width === next.width && current.height === next.height ? current : next);
      // On narrow screens the nav scrolls sideways; keep the active link visible without touching the page scroll.
      if (element.scrollWidth > element.clientWidth) {
        const left = Math.min(Math.max(element.scrollLeft, next.x + next.width - element.clientWidth + 8), next.x - 8);
        if (left !== element.scrollLeft) element.scrollTo({ left, behavior: "smooth" });
      }
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [nav, active]);

  return box;
}

export function SettingsPage() {
  const { services } = useApp();
  const dataset = useDataset();
  const sections = SECTIONS.filter((section) => (!section.sync || services.sync.mode !== "local") && (!section.desktop || services.desktop));
  const [active, jump] = useActiveSection(sections.map((section) => section.id));
  const nav = useRef<HTMLElement>(null);
  const pill = useNavPill(nav, active);

  return <div className="ot-page">
    <PageHeader title="Settings" subtitle="Defaults, goals, your invoicing identity, and your data. Everything saves as you go."/>
    <div className="ot-settings">
      <motion.nav ref={nav} className="ot-settings__nav" aria-label="Settings sections" {...rise(1)}>
        {pill && <motion.span className="ot-settings__pill" aria-hidden initial={false} animate={pill} transition={{ type: "spring", stiffness: 480, damping: 42, mass: 0.9 }}/>}
        {sections.map((section) => {
          const on = section.id === active;
          return <a
            key={section.id}
            href={`#/settings`}
            data-section={section.id}
            className={`ot-settings__link ${on ? "is-on" : ""}`}
            aria-current={on ? "true" : undefined}
            onClick={(event) => { event.preventDefault(); jump(section.id); }}
          >
            <Icon name={section.icon}/>
            <span>{section.label}</span>
          </a>;
        })}
      </motion.nav>
      <div className="ot-settings__content">
        {services.sync.mode !== "local" && <SyncSection/>}
        {services.desktop && <DesktopSection desktop={services.desktop}/>}
        <General settings={dataset.settings}/>
        <Goals settings={dataset.settings}/>
        <Calendars settings={dataset.settings}/>
        <Invoicing settings={dataset.settings}/>
        <Data/>
      </div>
    </div>
  </div>;
}

function useSaveSettings() {
  const { act } = useApp();
  const factory = useFactory();
  return (patch: Record<string, unknown>, message: string) => (Object.keys(patch).length ? act([factory.op("settings", "settings", patch)], message) : Promise.resolve());
}

/* ---------------------------------------------------------------- calendar */

const ago = (ts: number, now: number) => {
  const minutes = Math.round((now - ts) / 60_000);
  return minutes < 1 ? "just now" : minutes < 60 ? `${minutes} min ago` : `at ${formatClock(ts)}`;
};

const ACCESS: Record<string, string> = { owner: "Yours", writer: "Can edit", reader: "Read-only", freeBusyReader: "Busy times only" };

/** Google Calendar: connecting the signed-in Google account, which calendars show, and where new events go. */
function GoogleCalendars({ settings }: { settings: Settings }) {
  const { now, google, notify } = useApp();
  const state = useGoogleState(google);
  const save = useSaveSettings();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!google || state.phase === "unavailable") {
    return <SettingRow label="Google Calendar" description="Create, change, and delete events in your Google calendars from here. It works through ontrack's server, so it needs you signed in." wide>
      <p className="ot-note"><Icon name="info"/><span>Google Calendar isn't available on this device right now.</span></p>
    </SettingRow>;
  }
  if (state.phase === "loading" || (state.phase === "disconnected" && state.syncing)) {
    return <SettingRow label="Google Calendar" description="Checking the connection…"><span/></SettingRow>;
  }
  if (state.phase === "disconnected") {
    return <SettingRow label="Google Calendar" description="See your Google events beside your time, and create, move, change, or delete them here. ontrack asks Google for access to your events and the list of your calendars, on the Google account you signed in with." wide>
      <div className="ot-google">
        <Button tone="primary" icon="calendar-plus" onClick={() => google.connect()}>Connect Google Calendar</Button>
        <p className="ot-feed-help">Google shows a "Google hasn't verified this app" screen the first time: choose Advanced, then Go to ontrack. Disconnect any time here or in your Google Account.</p>
      </div>
    </SettingRow>;
  }

  const enabled = new Set(enabledCalendars(state.calendars, settings.googleCalendars).map((calendar) => calendar.id));
  const destinations = destinationOptions(state.calendars);
  const disconnect = async () => {
    setBusy(true);
    try {
      await google.disconnect();
      await save({ googleCalendars: {}, newEventsCalendar: null }, "Google Calendar disconnected");
    } catch (cause) {
      notify(cause instanceof Error ? cause.message : "Google could not be reached to disconnect");
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };
  const status = state.phase === "reconnect" ? "Needs connecting again"
    : state.syncing ? "Syncing…"
    : state.error ? state.error
    : state.syncedAt ? `Updated ${ago(state.syncedAt, now)}` : "Connected";

  return <>
    <SettingRow label="Google Calendar" description={<>{state.email ? <>Connected as <strong>{state.email}</strong>. </> : null}Events from the calendars switched on below show on the Calendar and Today, and changes made here go straight to Google.</>} wide>
      <div className="ot-google">
        <div className="ot-google__status">
          <span className={`ot-feed__status ${state.phase === "reconnect" || state.error ? "is-error" : ""}`}>{status}{state.pending.length ? ` · ${state.pending.length === 1 ? "1 change waits" : `${state.pending.length} changes wait`} to be sent` : ""}</span>
          <span className="ot-form__spacer"/>
          {state.phase === "reconnect"
            ? <Button tone="primary" onClick={() => google.connect()}>Reconnect</Button>
            : <IconButton className="is-ghost" label="Refresh from Google" icon="refresh" disabled={state.syncing} onClick={() => void google.sync({ force: true })}/>}
          <Button tone="ghost" disabled={busy} onClick={() => (state.pending.length ? setConfirming(true) : void disconnect())}>Disconnect</Button>
        </div>
        <div className="ot-feeds">
          {state.calendars.map((calendar) => <div key={calendar.id} className={`ot-feed ot-feed--google ${enabled.has(calendar.id) ? "" : "is-off"}`}>
            <span className="ot-google__dot" style={{ background: calendar.color }} aria-hidden="true"/>
            <div className="ot-feed__main">
              <strong className="ot-google__name">{calendar.name}</strong>
              <span className="ot-feed__status">{calendar.primary ? "Main calendar · " : ""}{ACCESS[calendar.accessRole] ?? calendar.accessRole}</span>
            </div>
            <Switch aria-label={`Show ${calendar.name}`} checked={enabled.has(calendar.id)} onChange={(on) => void save({ googleCalendars: { ...settings.googleCalendars, [calendar.id]: on } }, on ? `Showing ${calendar.name}` : `Hid ${calendar.name}`)}/>
          </div>)}
          {!state.calendars.length && <p className="ot-feed-help">{state.syncing ? "Loading your calendars…" : "No calendars yet. Refresh to load them."}</p>}
        </div>
      </div>
    </SettingRow>
    {destinations.length > 1 && <SettingRow label="New events go to" description="Where events you plan here go: dragging on the Calendar, the Command Bar, and the assistant. The new-event popover can always choose another.">
      <Select aria-label="New events go to" value={defaultDestination(settings.newEventsCalendar, destinations)} onChange={(value) => void save({ newEventsCalendar: destinationOf(value) }, `New events go to ${destinations.find((option) => option.value === value)?.label ?? "ontrack"}`)} options={destinations}/>
    </SettingRow>}
    <AnimatePresence>
      {confirming && <Modal key="disconnect" title="Disconnect Google Calendar?" icon="calendar" description={`${state.pending.length === 1 ? "1 change hasn't" : `${state.pending.length} changes haven't`} reached Google yet. Disconnecting drops ${state.pending.length === 1 ? "it" : "them"} and removes Google events from this device. Your events stay in Google.`} onClose={() => setConfirming(false)}>
        <div className="ot-form__actions">
          <span className="ot-form__spacer"/>
          <Button onClick={() => setConfirming(false)}>Keep connected</Button>
          <Button tone="primary" disabled={busy} onClick={() => void disconnect()}>Disconnect</Button>
        </div>
      </Modal>}
    </AnimatePresence>
  </>;
}

function Calendars({ settings }: { settings: Settings }) {
  const save = useSaveSettings();
  return <Panel id="settings-calendars" title="Calendar" icon="calendar" description="How the Calendar lays out your week, and your Google calendars beside your plans." index={3}>
    <div className="ot-rows">
      <SettingRow label="Weekends" description="Weekends show narrower in the week view. Hide them to see five wider days.">
        <Switch aria-label="Hide weekends" label="Hide weekends" checked={settings.hideWeekends} onChange={(hideWeekends) => void save({ hideWeekends }, hideWeekends ? "Weekends hidden" : "Weekends shown")}/>
      </SettingRow>
      <GoogleCalendars settings={settings}/>
    </div>
  </Panel>;
}

/* ---------------------------------------------------------------- general */

const THEMES: Array<{ value: Theme; label: string; icon: IconName }> = [
  { value: "system", label: "Match system", icon: "monitor" },
  { value: "light", label: "Light", icon: "sun" },
  { value: "dark", label: "Dark", icon: "moon" }
];

/** A tiny drawing of the Today screen in a theme, so the choice reads at a glance. */
function ThemePreview({ theme }: { theme: Theme }) {
  return <span className={`ot-theme-preview is-${theme}`} aria-hidden="true">
    {theme === "system" ? <><span className="ot-theme-preview__half is-light"><Mini/></span><span className="ot-theme-preview__half is-dark"><Mini/></span></> : <Mini/>}
  </span>;
}

function Mini() {
  return <span className="ot-mini">
    <span className="ot-mini__bar"/>
    <span className="ot-mini__card"><i/><b/></span>
    <span className="ot-mini__rows"><i style={{ width: "62%" }}/><i style={{ width: "38%", marginLeft: "18%" }}/><i className="is-live" style={{ width: "26%", marginLeft: "50%" }}/></span>
  </span>;
}

function General({ settings }: { settings: Settings }) {
  const { store } = useApp();
  const save = useSaveSettings();
  const setTheme = (theme: Theme) => void store.getState().dispatch([store.getState().factory.op("settings", "settings", { theme })], { undoable: false });

  return <Panel id="settings-general" title="General" icon="monitor" description="How ontrack looks and what it falls back to." index={2}>
    <div className="ot-rows">
      <SettingRow label="Appearance" description="Dark and light are both first-class." wide>
        <div className="ot-themes" role="radiogroup" aria-label="Theme">
          {THEMES.map((option) => {
            const on = settings.theme === option.value;
            return <button key={option.value} type="button" role="radio" aria-checked={on} className={`ot-theme ${on ? "is-on" : ""}`} onClick={() => setTheme(option.value)}>
              <ThemePreview theme={option.value}/>
              <span className="ot-theme__label"><Icon name={option.icon}/>{option.label}</span>
              <AnimatePresence>{on && <motion.span className="ot-theme__check" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} transition={{ type: "spring", stiffness: 600, damping: 26 }}><Icon name="check" weight="bold"/></motion.span>}</AnimatePresence>
            </button>;
          })}
        </div>
      </SettingRow>
      <SettingRow label="Default currency" description="For projects and clients without their own. Logged entries keep theirs.">
        <Select
          aria-label="Default currency"
          value={settings.defaultCurrency}
          options={currencyOptions([settings.defaultCurrency])}
          onChange={(code) => void save({ defaultCurrency: code, defaultHourlyRate: convertTyped(settings.defaultHourlyRate, settings.defaultCurrency, code) }, `Default currency set to ${code}`)}
        />
      </SettingRow>
      <SettingRow label="Default hourly rate" description="Used when neither the project nor its client has a rate.">
        <MoneyField
          aria-label="Default hourly rate"
          amount={settings.defaultHourlyRate}
          currency={settings.defaultCurrency}
          perHour
          placeholder="None"
          onCommit={(defaultHourlyRate) => save({ defaultHourlyRate }, defaultHourlyRate === null ? "Default rate cleared" : `Default rate set to ${formatMoney({ amount: defaultHourlyRate, currency: settings.defaultCurrency })}/h`)}
        />
      </SettingRow>
    </div>
  </Panel>;
}

/* ---------------------------------------------------------------- goals */

function Goals({ settings }: { settings: Settings }) {
  const { now } = useApp();
  const dataset = useDataset();
  const save = useSaveSettings();
  const used = new Set<string>([settings.defaultCurrency, ...Object.keys(settings.monthlyGoals)]);
  for (const project of Object.values(dataset.projects)) if (isLive(project)) used.add(projectCurrency(dataset, project));
  const currencies = [...used].sort((a, b) => (a === "USD" ? -1 : b === "USD" ? 1 : a.localeCompare(b)));
  const earned = earnedByCurrency(dataset, monthRange(now), now);

  return <Panel id="settings-goals" title="Monthly goals" icon="target" description="Optional, one per currency. Today shows the progress; there are no alerts." index={3}>
    <div className="ot-rows">
      {currencies.map((code) => {
        const goal = settings.monthlyGoals[code] ?? null;
        const amount = earned[code] ?? 0;
        const progress = goal ? Math.min(1, amount / goal) : 0;
        return <SettingRow key={code} label={<span className="ot-goal__code">{code}<span>per month</span></span>} description={<span className="ot-goal">
          <span>{formatMoney({ amount, currency: code })} earned in {formatMonth(now)}{goal ? <> · <strong className="ot-mono">{Math.round(progress * 100)}%</strong></> : ""}</span>
          <AnimatePresence initial={false}>{goal && <motion.span className="ot-meter" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 6 }} exit={{ opacity: 0, height: 0 }}><i style={{ transform: `scaleX(${progress})` }}/></motion.span>}</AnimatePresence>
        </span>}>
          <MoneyField
            aria-label={`${code} monthly goal`}
            amount={goal}
            currency={code}
            placeholder="No goal"
            onCommit={(next) => {
              const goals = { ...settings.monthlyGoals };
              if (next) goals[code] = next;
              else delete goals[code];
              return save({ monthlyGoals: goals }, next ? `${code} goal set to ${formatMoney({ amount: next, currency: code })}` : `${code} goal removed`);
            }}
          />
        </SettingRow>;
      })}
    </div>
  </Panel>;
}

/* ---------------------------------------------------------------- invoicing */

function Invoicing({ settings }: { settings: Settings }) {
  const dataset = useDataset();
  const save = useSaveSettings();
  const profile = settings.businessProfile;
  const currencies = new Set<string>([settings.defaultCurrency, ...Object.keys(profile.paymentInstructions).filter((key) => key !== "default")]);
  for (const project of Object.values(dataset.projects)) if (isLive(project)) currencies.add(projectCurrency(dataset, project));
  const overrides = [...currencies].sort();
  const [showOverrides, setShowOverrides] = useState(() => overrides.some((code) => profile.paymentInstructions[code]));

  const saveProfile = (next: BusinessProfile, message: string) => {
    const paymentInstructions = Object.fromEntries(Object.entries(next.paymentInstructions).filter(([, text]) => text.trim()));
    return save({ businessProfile: { ...next, paymentInstructions } }, message);
  };
  const field = (key: "name" | "email" | "taxId" | "address", label: string) => ({
    value: profile[key],
    onCommit: async (text: string) => {
      const value = text.trim();
      if (value !== profile[key]) await saveProfile({ ...profile, [key]: value }, `${label} saved`);
    }
  });
  const instruction = (key: string, label: string) => ({
    value: profile.paymentInstructions[key] ?? "",
    onCommit: async (text: string) => {
      if (text.trim() !== (profile.paymentInstructions[key] ?? "")) await saveProfile({ ...profile, paymentInstructions: { ...profile.paymentInstructions, [key]: text.trim() } }, `${label} saved`);
    }
  });
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const uploadLogo = async (file: File) => {
    setLogoBusy(true);
    setLogoError(null);
    try {
      await saveProfile({ ...profile, logo: await prepareLogo(file) }, "Logo saved");
    } catch (error) {
      setLogoError(error instanceof Error ? error.message : "That image could not be used.");
    } finally {
      setLogoBusy(false);
    }
  };
  const sample = `${settings.invoicePrefix}${new Date().getFullYear()}-001`;

  return <Panel id="settings-invoicing" title="Invoicing" icon="receipt" description="Your business identity, as it appears on invoices." index={4}>
    <div className="ot-grid-2">
      <Field label="Logo" optional hint="PNG, JPEG, or WebP. Sent invoices keep the logo they went out with." className="ot-field--logo">
        <div className="ot-logo-field">
          <LogoPicker logo={profile.logo} busy={logoBusy} onFile={(file) => void uploadLogo(file)} onRemove={() => { setLogoError(null); void saveProfile({ ...profile, logo: null }, "Logo removed"); }}/>
          <FieldError>{logoError}</FieldError>
        </div>
      </Field>
      <Field label="Business name"><EditableText {...field("name", "Business name")} maxLength={200} placeholder="Your name or company"/></Field>
      <Field label="Email"><EditableText {...field("email", "Email")} type="email" maxLength={320} placeholder="you@example.com"/></Field>
      <Field label="Tax ID" optional hint="RIF, RUT, EIN, VAT…"><EditableText {...field("taxId", "Tax ID")} maxLength={100}/></Field>
      <Field label="Invoice number prefix" optional hint={<>Next invoice looks like <code className="ot-code">{sample}</code></>}>
        <EditableText value={settings.invoicePrefix} maxLength={20} placeholder="e.g. OT-" onCommit={async (text) => {
          if (text.trim() !== settings.invoicePrefix) await save({ invoicePrefix: text.trim() }, "Invoice prefix saved");
        }}/>
      </Field>
      <Field label="Address" className="ot-grid-2__wide"><EditableText {...field("address", "Address")} multiline rows={3} maxLength={1000}/></Field>
      <Field label="Payment instructions" className="ot-grid-2__wide" hint="Bank details or payment links, printed at the bottom of every invoice.">
        <EditableText {...instruction("default", "Payment instructions")} multiline rows={3} maxLength={2000} placeholder="Bank, account number, or a payment link"/>
      </Field>
    </div>
    <div className="ot-disclosure">
      <button type="button" className="ot-disclosure__toggle" aria-expanded={showOverrides} onClick={() => setShowOverrides(!showOverrides)}>
        <Icon name="caret-right" weight="bold" className="ot-disclosure__caret"/>
        Different instructions per currency
        <span className="ot-disclosure__meta">{overrides.filter((code) => profile.paymentInstructions[code]).length || ""}</span>
      </button>
      <AnimatePresence initial={false}>
        {showOverrides && <motion.div className="ot-disclosure__body" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}>
          <div className="ot-stack">
            {overrides.map((code) => <Field key={code} label={`${code} invoices`} optional>
              <EditableText {...instruction(code, `${code} instructions`)} multiline rows={2} maxLength={2000} placeholder="Overrides the instructions above"/>
            </Field>)}
          </div>
        </motion.div>}
      </AnimatePresence>
    </div>
  </Panel>;
}

/* ---------------------------------------------------------------- data */

function Data() {
  const { now, act, openImport } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const [pending, setPending] = useState<Backup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const trashCount = trashItems(dataset, now).length;
  const entryCount = Object.values(dataset.timeEntries).filter(isLive).length;

  const download = () => {
    const blob = new Blob([JSON.stringify(createBackup(dataset, Date.now()), null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ontrack-backup-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const read = async (file: File) => {
    setError(null);
    setBusy(true);
    try {
      const parsed = backupSchema.safeParse(JSON.parse(await file.text()));
      if (!parsed.success) return setError("This is not an ontrack backup, or it is from a newer version.");
      setPending(parsed.data);
    } catch {
      setError("The file could not be read as JSON.");
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    if (!pending) return;
    await act(restoreOperations(pending, factory), `Restored ${pending.timeEntries.length} entries from the backup`, { undoable: false });
    setPending(null);
  };

  return <Panel id="settings-data" title="Your data" icon="database" description="It lives on your devices first. Take it with you any time." index={5}>
    <div className="ot-tiles">
      <div className="ot-tile">
        <span className="ot-tile__icon"><Icon name="download"/></span>
        <div className="ot-tile__text"><strong>Backup</strong><p>Everything in one JSON file: {entryCount} {entryCount === 1 ? "entry" : "entries"}, projects, clients, and settings.</p></div>
        <Button icon="download" onClick={download}>Download backup</Button>
      </div>
      <div className="ot-tile">
        <span className="ot-tile__icon"><Icon name="refresh"/></span>
        <div className="ot-tile__text"><strong>Restore</strong><p>Writes a backup over matching items here. Anything not in the backup stays.</p></div>
        <FileDrop accept="application/json,.json" icon="file-up" title="Drop a backup, or browse" hint=".json from ontrack" busy={busy} onFile={(file) => void read(file)}/>
        <FieldError>{error}</FieldError>
      </div>
      <div className="ot-tile">
        <span className="ot-tile__icon"><Icon name="upload"/></span>
        <div className="ot-tile__text"><strong>solidtime</strong><p>Import an organization export. Importing the same file again updates instead of duplicating.</p></div>
        <Button icon="download" onClick={openImport}>Import from solidtime</Button>
      </div>
      <a className="ot-tile ot-tile--link" href={href({ page: "trash" })}>
        <span className="ot-tile__icon"><Icon name="trash"/>{trashCount > 0 && <span className="ot-tile__badge ot-mono">{trashCount}</span>}</span>
        <div className="ot-tile__text"><strong>Trash</strong><p>{trashCount ? `${trashCount} ${trashCount === 1 ? "item" : "items"}, kept for 30 days.` : "Empty. Deleted items wait here for 30 days."}</p></div>
        <span className="ot-tile__go">Open Trash<Icon name="arrow-right" weight="bold"/></span>
      </a>
    </div>
    <AnimatePresence>
      {pending && <Modal key="restore" title="Restore this backup?" icon="refresh" description={`Made ${new Date(pending.exportedAt).toLocaleString()}.`} onClose={() => setPending(null)}>
        <div className="ot-form">
          <dl className="ot-summary-grid">
            <div><dt>Clients</dt><dd className="ot-mono">{pending.clients.length}</dd></div>
            <div><dt>Projects</dt><dd className="ot-mono">{pending.projects.length}</dd></div>
            <div><dt>Tasks</dt><dd className="ot-mono">{pending.tasks.length}</dd></div>
            <div><dt>Entries</dt><dd className="ot-mono">{pending.timeEntries.length}</dd></div>
          </dl>
          <p className="ot-note"><Icon name="warning"/>Matching items here will be replaced by the backup's version. This can't be undone.</p>
          <div className="ot-form__actions"><span className="ot-form__spacer"/><Button onClick={() => setPending(null)}>Cancel</Button><Button tone="primary" icon="refresh" onClick={() => void restore()}>Restore</Button></div>
        </div>
      </Modal>}
    </AnimatePresence>
  </Panel>;
}

/* ---------------------------------------------------------------- desktop */

function DesktopSection({ desktop }: { desktop: DesktopServices }) {
  const action = updateAction(desktop.updates);
  const checking = desktop.updates.phase === "checking" || desktop.updates.phase === "downloading" || desktop.updates.phase === "installing";
  return <Panel id="settings-desktop" title="Desktop" icon="command" description="The tray, shortcuts that work from any app, and updates for this computer." index={2}>
    <div className="ot-rows">
      <SettingRow label={`Version ${desktop.version}`} description={updateStatus(desktop.updates)}>
        {action
          ? <Button tone="primary" icon="download" onClick={desktop.installUpdate} disabled={checking}>{action}</Button>
          : <Button icon="refresh" onClick={desktop.checkForUpdates} disabled={checking}>Check for updates</Button>}
      </SettingRow>
      {desktop.openAtLogin !== null && <SettingRow label="Open at login" description="Starts ontrack in the tray when you sign in to this computer, so the Timer and shortcuts are always there.">
        <Switch aria-label="Open at login" checked={desktop.openAtLogin} onChange={desktop.setOpenAtLogin}/>
      </SettingRow>}
      {desktop.shortcuts.map((shortcut) => <SettingRow
        key={shortcut.label}
        label={shortcut.label}
        description={shortcut.registered ? "Works from any app, even with ontrack in the tray." : "Another app is using this shortcut, so it doesn't reach ontrack."}
      >
        <kbd className={`ot-shortcut ${shortcut.registered ? "" : "is-taken"}`.trim()}>{shortcut.keys}</kbd>
      </SettingRow>)}
    </div>
  </Panel>;
}

/* ---------------------------------------------------------------- sync */

function SyncSection() {
  const { services } = useApp();
  const { mode, status, account, signIn, signOut, syncNow } = services.sync;
  const when = status.lastSyncedAt ? formatClock(status.lastSyncedAt) : null;
  const phase = mode === "signed-out" ? "local" : status.phase === "error" ? "error" : status.phase === "offline" ? "offline" : status.phase === "syncing" ? "syncing" : "ok";
  const title = { local: "This device only", error: "Sync problem", offline: "Offline", syncing: "Syncing…", ok: "Up to date" }[phase];
  const detail = mode === "signed-out"
    ? "Sign in to keep your time in sync across your computers and phone. Everything already here is uploaded the first time."
    : `${status.message ?? (when ? `Last synced at ${when}.` : "Waiting for the first sync.")}${status.pending ? ` ${status.pending} ${status.pending === 1 ? "change" : "changes"} waiting to upload.` : ""}`;

  return <Panel id="settings-sync" title="Sync" icon="cloud" description="Your data stays on this device and syncs through your account." index={1}>
    <div className={`ot-sync-card is-${phase}`}>
      <span className="ot-sync-card__orb"><Icon name={phase === "ok" ? "cloud-check" : phase === "error" ? "warning" : "cloud"}/></span>
      <div className="ot-sync-card__text">
        <strong>{title}</strong>
        <p>{detail}</p>
        {mode === "signed-in" && <span className="ot-sync-card__account"><Icon name="user"/>{account ?? "Signed in"}</span>}
      </div>
      <div className="ot-sync-card__actions">
        {mode === "signed-out" && signIn && <Button tone="primary" icon="sign-in" onClick={signIn}>Sign in</Button>}
        {mode === "signed-in" && syncNow && <Button icon="refresh" onClick={syncNow} disabled={phase === "syncing"}>Sync now</Button>}
        {mode === "signed-in" && signOut && <Button tone="ghost" icon="sign-out" onClick={signOut}>Sign out</Button>}
      </div>
    </div>
  </Panel>;
}
